import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '../../../components/feature/NavBar';
import Button from '../../../components/base/Button';

interface Player {
  id: string;
  name: string;
  score: number;
  color: string;
  isTeacher: boolean;
}

interface GameState {
  players: Player[];
  currentPlayer: number;
  gameStarted: boolean;
  winner: Player | null;
  gameId: string;
  difficulty: string;
  currentWord: string;
  currentWordIndex: number;
  timeLeft: number;
  round: number;
  maxRounds: number;
}

interface Letter {
  id: string;
  letter: string;
  correctPosition: number;
  currentPosition: number | null;
  isDragging: boolean;
}

export default function WordPuzzle() {
  const navigate = useNavigate();
  const [gameState, setGameState] = useState<GameState>({
    players: [],
    currentPlayer: 0,
    gameStarted: false,
    winner: null,
    gameId: '',
    difficulty: 'kindergarten',
    currentWord: '',
    currentWordIndex: 0,
    timeLeft: 60,
    round: 1,
    maxRounds: 5
  });
  
  const [letters, setLetters] = useState<Letter[]>([]);
  const [dropZones, setDropZones] = useState<(string | null)[]>([]);
  const [playerName, setPlayerName] = useState('');
  const [isTeacher, setIsTeacher] = useState(false);
  const [gameId, setGameId] = useState('');
  const [showJoinModal, setShowJoinModal] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [showGameCodeModal, setShowGameCodeModal] = useState(false);
  const [selectedDifficulty, setSelectedDifficulty] = useState('kindergarten');
  const [showCelebration, setShowCelebration] = useState(false);
  const [isWordComplete, setIsWordComplete] = useState(false);
  const [showCorrectAnimation, setShowCorrectAnimation] = useState(false);
  const [draggedLetter, setDraggedLetter] = useState<string | null>(null);
  
  // Player colors
  const playerColors = [
    { bg: 'bg-red-500', border: 'border-red-600', name: 'Red' },
    { bg: 'bg-blue-500', border: 'border-blue-600', name: 'Blue' },
    { bg: 'bg-green-500', border: 'border-green-600', name: 'Green' },
    { bg: 'bg-yellow-500', border: 'border-yellow-600', name: 'Yellow' },
    { bg: 'bg-purple-500', border: 'border-purple-600', name: 'Purple' },
    { bg: 'bg-pink-500', border: 'border-pink-600', name: 'Pink' }
  ];
  
  // Difficulty levels with words
  const difficultyLevels = {
    kindergarten: {
      name: 'Kindergarten',
      description: 'Simple 2-3 letter words',
      words: ['cat', 'dog', 'sun', 'run', 'hat', 'bat', 'cup', 'bug', 'pig', 'fox', 'box', 'bed', 'leg', 'net', 'web'],
      timeLimit: 90
    },
    year1: {
      name: 'Year 1', 
      description: 'CVC words and basic sight words',
      words: ['fish', 'ship', 'frog', 'tree', 'bike', 'cake', 'game', 'blue', 'jump', 'play', 'read', 'make', 'time', 'like', 'come'],
      timeLimit: 75
    },
    year2: {
      name: 'Year 2',
      description: 'Consonant blends and digraphs', 
      words: ['chair', 'shark', 'think', 'whale', 'school', 'friend', 'plants', 'bright', 'string', 'splash', 'bridge', 'spring', 'twelve', 'thumb', 'phone'],
      timeLimit: 60
    },
    year3: {
      name: 'Year 3',
      description: 'Long vowel sounds and magic e',
      words: ['magic', 'brave', 'smile', 'grape', 'plane', 'snake', 'spine', 'globe', 'flute', 'frame', 'shade', 'prize', 'knife', 'whale', 'theme'],
      timeLimit: 60
    },
    year4: {
      name: 'Year 4', 
      description: 'Complex words and suffixes',
      words: ['jumping', 'teacher', 'quickly', 'careful', 'painting', 'singing', 'helping', 'walking', 'playing', 'reading', 'kindly', 'slowly', 'loudly', 'sadly', 'gladly'],
      timeLimit: 45
    },
    year5: {
      name: 'Year 5',
      description: 'Advanced vocabulary and prefixes',
      words: ['replay', 'unhappy', 'disappear', 'mistake', 'rebuild', 'unfair', 'discover', 'mislead', 'return', 'unlock', 'discuss', 'misplace', 'review', 'unable', 'dismiss'],
      timeLimit: 45
    },
    year6: {
      name: 'Year 6',
      description: 'Complex multi-syllable words', 
      words: ['incredible', 'adventure', 'mystery', 'technology', 'imagination', 'celebrate', 'education', 'discovery', 'beautiful', 'different', 'important', 'wonderful', 'experience', 'excellent', 'dangerous'],
      timeLimit: 30
    }
  };
  
  const getCurrentWordList = () => {
    return difficultyLevels[gameState.difficulty as keyof typeof difficultyLevels]?.words || difficultyLevels.kindergarten.words;
  };
  
  const getTimeLimit = () => {
    return difficultyLevels[gameState.difficulty as keyof typeof difficultyLevels]?.timeLimit || 60;
  };
  
  // Move nextRound function above useEffect and wrap in useCallback
  const nextRound = useCallback(() => {
    if (gameState.round >= gameState.maxRounds) {
      endGame();
      return;
    }
    
    const wordList = getCurrentWordList();
    const nextWordIndex = (gameState.currentWordIndex + 1) % wordList.length;
    const nextWord = wordList[nextWordIndex];
    
    // Reset word state
    setIsWordComplete(false);
    setShowCorrectAnimation(false);
    
    setGameState(prev => ({
      ...prev,
      currentPlayer: (prev.currentPlayer + 1) % prev.players.length,
      currentWord: nextWord,
      currentWordIndex: nextWordIndex,
      timeLeft: getTimeLimit(),
      round: prev.round + 1
    }));
    
    initializeWord(nextWord);
  }, [gameState.round, gameState.maxRounds, gameState.currentWordIndex, gameState.players.length, gameState.difficulty]);
  
  // Improved endGame function with better winner determination
  const endGame = useCallback(() => {
    const sortedPlayers = [...gameState.players].sort((a, b) => {
      // Primary sort by score (highest first)
      if (b.score !== a.score) {
        return b.score - a.score;
      }
      // If scores are tied, the teacher wins (gives teacher slight advantage)
      if (a.isTeacher && !b.isTeacher) return -1;
      if (!a.isTeacher && b.isTeacher) return 1;
      // If still tied, maintain original order
      return 0;
    });
    
    const winner = sortedPlayers[0];
    
    // Only declare winner if they have a positive score
    if (winner && winner.score > 0) {
      setGameState(prev => ({ ...prev, winner }));
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 5000);
    } else {
      // If no one scored, it's a draw - pick the first player as "winner" but show different message
      setGameState(prev => ({ ...prev, winner: { ...winner, name: "Nobody" } }));
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 5000);
    }
  }, [gameState.players]);
  
  // Fixed timer effect with proper dependencies and cleanup
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (gameState.gameStarted && gameState.timeLeft > 0 && !isWordComplete && !gameState.winner) {
      timer = setInterval(() => {
        setGameState(prev => {
          if (prev.timeLeft > 0) {
            return { ...prev, timeLeft: prev.timeLeft - 1 };
          }
          return prev;
        });
      }, 1000);
    }
    
    // Check if time is up
    if (gameState.timeLeft === 0 && !isWordComplete && gameState.gameStarted && !gameState.winner) {
      const timeoutId = setTimeout(() => {
        nextRound();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
    
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [gameState.gameStarted, gameState.timeLeft, isWordComplete, gameState.winner, nextRound]);
  
  const generateGameId = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };
  
  const shuffleArray = (array: any[]) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };
  
  // Enhanced word selection with better randomization
  const getRandomWordIndex = (wordList: string[], currentIndex: number) => {
    if (wordList.length <= 1) return 0;
    let newIndex;
    do {
      newIndex = Math.floor(Math.random() * wordList.length);
    } while (newIndex === currentIndex);
    return newIndex;
  };
  
  const initializeWord = (word: string) => {
    const wordLetters = word.split('').map((letter, index) => ({
      id: `${letter}-${index}-${Math.random()}`,
      letter: letter.toUpperCase(),
      correctPosition: index,
      currentPosition: null,
      isDragging: false
    }));
    
    setLetters(shuffleArray(wordLetters));
    setDropZones(new Array(word.length).fill(null));
    setIsWordComplete(false);
  };
  
  const joinGame = () => {
    if (!playerName.trim()) return;
    
    const newGameId = gameId || generateGameId();
    const playerId = Math.random().toString(36).substring(2, 15);
    const colorIndex = gameState.players.length % playerColors.length;
    
    const newPlayer: Player = {
      id: playerId,
      name: playerName.trim(),
      score: 0,
      color: playerColors[colorIndex].bg,
      isTeacher: isTeacher
    };
    
    const wordList = difficultyLevels[selectedDifficulty as keyof typeof difficultyLevels]?.words || difficultyLevels.kindergarten.words;
    // Start with a random word instead of always the first one
    const randomIndex = Math.floor(Math.random() * wordList.length);
    const firstWord = wordList[randomIndex];
    
    setGameState(prev => ({
      ...prev,
      players: [...prev.players, newPlayer],
      gameId: newGameId,
      difficulty: selectedDifficulty,
      currentWord: firstWord,
      currentWordIndex: randomIndex,
      timeLeft: difficultyLevels[selectedDifficulty as keyof typeof difficultyLevels]?.timeLimit || 60
    }));
    
    initializeWord(firstWord);
    setGameId(newGameId);
    setShowJoinModal(false);
    setIsConnected(true);
    
    if (isTeacher && !gameId) {
      setShowGameCodeModal(true);
    }
  };
  
  const copyGameCode = async () => {
    try {
      await navigator.clipboard.writeText(gameState.gameId);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = gameState.gameId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };
  
  const handleDragStart = (e: React.DragEvent, letterId: string) => {
    setDraggedLetter(letterId);
    setLetters(prev => prev.map(l => 
      l.id === letterId ? { ...l, isDragging: true } : l
    ));
  };
  
  const handleDragEnd = () => {
    setDraggedLetter(null);
    setLetters(prev => prev.map(l => ({ ...l, isDragging: false })));
  };
  
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };
  
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (!draggedLetter) return;
    
    const draggedLetterObj = letters.find(l => l.id === draggedLetter);
    if (!draggedLetterObj) return;
    
    // Remove letter from its current position if it was already placed
    const newDropZones = [...dropZones];
    const currentPos = draggedLetterObj.currentPosition;
    if (currentPos !== null) {
      newDropZones[currentPos] = null;
    }
    
    // If there's already a letter in the target position, move it back to letters area
    if (newDropZones[dropIndex]) {
      setLetters(prev => prev.map(l => 
        l.id === newDropZones[dropIndex] ? { ...l, currentPosition: null } : l
      ));
    }
    
    // Place the dragged letter in the new position
    newDropZones[dropIndex] = draggedLetter;
    setDropZones(newDropZones);
    
    // Update letter position
    setLetters(prev => prev.map(l => 
      l.id === draggedLetter ? { ...l, currentPosition: dropIndex } : l
    ));
    
    // Check if word is complete and correct
    checkWordCompletion(newDropZones);
  };
  
  const handleLetterReturn = (letterId: string) => {
    const letter = letters.find(l => l.id === letterId);
    if (!letter || letter.currentPosition === null) return;
    
    const newDropZones = [...dropZones];
    newDropZones[letter.currentPosition] = null;
    setDropZones(newDropZones);
    
    setLetters(prev => prev.map(l => 
      l.id === letterId ? { ...l, currentPosition: null } : l
    ));
  };
  
  const checkWordCompletion = (zones: (string | null)[]) => {
    if (zones.every(zone => zone !== null)) {
      const placedWord = zones.map(zoneId => {
        const letter = letters.find(l => l.id === zoneId);
        return letter?.letter || '';
      }).join('');
      
      if (placedWord === gameState.currentWord.toUpperCase()) {
        setIsWordComplete(true);
        setShowCorrectAnimation(true);
        
        // Improved scoring system - more reasonable points
        const basePoints = 5; // Each word is worth 5 points
        const timeBonus = Math.floor(gameState.timeLeft / 5); // 1 point per 5 seconds remaining
        const totalPoints = basePoints + timeBonus;
        
        setGameState(prev => ({
          ...prev,
          players: prev.players.map((p, index) => 
            index === prev.currentPlayer ? { ...p, score: p.score + totalPoints } : p
          )
        }));
        
        setTimeout(() => {
          setShowCorrectAnimation(false);
          nextRound();
        }, 2000);
      }
    }
  };
  
  const resetGame = () => {
    const wordList = getCurrentWordList();
    // Start with a new random word for reset
    const randomIndex = Math.floor(Math.random() * wordList.length);
    const firstWord = wordList[randomIndex];
    
    setGameState(prev => ({
      ...prev,
      players: prev.players.map(p => ({ ...p, score: 0 })),
      currentPlayer: 0,
      winner: null,
      gameStarted: false,
      currentWord: firstWord,
      currentWordIndex: randomIndex,
      timeLeft: getTimeLimit(),
      round: 1
    }));
    
    initializeWord(firstWord);
    setIsWordComplete(false);
    setShowCorrectAnimation(false);
    setShowCelebration(false);
  };
  
  const startGame = () => {
    const wordList = getCurrentWordList();
    // Use current word or get a new random one if needed
    const currentWord = gameState.currentWord || wordList[Math.floor(Math.random() * wordList.length)];
    
    setGameState(prev => ({ 
      ...prev, 
      gameStarted: true,
      currentWord: currentWord,
      timeLeft: getTimeLimit()
    }));
    
    initializeWord(currentWord);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      <NavBar />
      
      {/* Celebration Animation */}
      {showCelebration && gameState.winner && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-[60] pointer-events-none">
          <div className="text-center animate-bounce">
            <div className="text-8xl mb-4 animate-pulse">🎉</div>
            <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-lg">
              <div className="text-6xl mb-4">{gameState.winner.name === "Nobody" ? "🤷‍♂️" : "🏆"}</div>
              <h2 className="text-4xl font-bold text-purple-600 mb-2 animate-pulse">
                {gameState.winner.name === "Nobody" ? "It's a Draw!" : `${gameState.winner.name} Wins!`}
              </h2>
              <p className="text-xl text-gray-600">
                {gameState.winner.name === "Nobody" ? "Better luck next time!" : "Word Master Champion!"}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Correct Word Animation */}
      {showCorrectAnimation && (
        <div className="fixed inset-0 bg-black/10 flex items-center justify-center z-50 pointer-events-none">
          <div className="bg-white rounded-3xl p-8 shadow-2xl animate-bounce">
            <div className="text-6xl mb-4 text-center">✨</div>
            <h3 className="text-3xl font-bold text-green-600 text-center">Perfect!</h3>
            <p className="text-lg text-gray-600 text-center mt-2">You spelled "{gameState.currentWord}" correctly!</p>
          </div>
        </div>
      )}
      
      {/* Join Game Modal */}
      {showJoinModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-puzzle-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800">Join Word Puzzle</h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <input
                  type="text"
                  placeholder="Enter your name"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  className="w-full p-4 border border-gray-300 rounded-xl text-center font-medium text-lg"
                />
              </div>
              
              {!isTeacher && (
                <div>
                  <input
                    type="text"
                    placeholder="Enter Game Code to Join"
                    value={gameId}
                    onChange={(e) => setGameId(e.target.value.toUpperCase())}
                    className="w-full p-4 border border-gray-300 rounded-xl text-center font-mono text-lg"
                  />
                </div>
              )}
              
              {isTeacher && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3 text-center">Choose Difficulty Level</label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(difficultyLevels).map(([key, level]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedDifficulty(key)}
                        className={`p-3 rounded-xl border-2 text-left transition-colors ${
                          selectedDifficulty === key
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-medium text-sm">{level.name}</div>
                        <div className="text-xs text-gray-500">{level.description}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={isTeacher}
                    onChange={(e) => setIsTeacher(e.target.checked)}
                    className="w-5 h-5 text-purple-500 rounded"
                  />
                  <span className="text-gray-700">I'm a teacher</span>
                </label>
              </div>
              
              <Button onClick={joinGame} disabled={!playerName.trim()} className="w-full py-4 text-lg">
                {isTeacher ? 'Create New Game' : gameId ? 'Join Game' : 'Enter Game Code or Check Teacher Box'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Game Code Share Modal */}
      {showGameCodeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-lg w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-share-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800">Share Game Code</h3>
              <p className="text-gray-600 mt-2">Send this code to your students</p>
            </div>
            
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl p-8 text-center">
                <div className="text-4xl font-bold text-white font-mono tracking-wider mb-2">
                  {gameState.gameId}
                </div>
                <p className="text-purple-100">Game Code</p>
              </div>
              
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <div className="text-lg font-medium text-purple-700 mb-1">
                  {difficultyLevels[gameState.difficulty as keyof typeof difficultyLevels]?.name}
                </div>
                <div className="text-sm text-gray-600">
                  {difficultyLevels[gameState.difficulty as keyof typeof difficultyLevels]?.description}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={copyGameCode} variant="outline" className="flex-1 py-3">
                  <i className="ri-file-copy-line mr-2"></i>
                  Copy Code
                </Button>
                <Button onClick={() => setShowGameCodeModal(false)} className="flex-1 py-3">
                  Got It!
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="pt-24 md:pt-28 pb-8 md:pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <button onClick={() => navigate('/games')} className="w-10 h-10 flex items-center justify-center">
              <i className="ri-arrow-left-line text-purple-600 text-xl"></i>
            </button>
            <div className="text-center">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800">Word Puzzle Challenge</h2>
              {isConnected && (
                <div className="flex items-center justify-center space-x-2 mt-2">
                  <p className="text-purple-600 font-mono font-bold text-lg">{gameState.gameId}</p>
                  <button 
                    onClick={() => setShowGameCodeModal(true)}
                    className="w-8 h-8 flex items-center justify-center text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    <i className="ri-share-line"></i>
                  </button>
                </div>
              )}
            </div>
            <img 
              src="https://readdy.ai/api/search-image?query=icon%2C%203D%20cartoon%20puzzle%20piece%2C%20vibrant%20purple%20and%20pink%20colors%20with%20soft%20gradients%2C%20minimalist%20design%2C%20smooth%20rounded%20shapes%2C%20subtle%20shading%2C%20no%20outlines%2C%20centered%20composition%2C%20isolated%20on%20white%20background%2C%20playful%20and%20friendly%20aesthetic%2C%20isometric%20perspective%2C%20high%20detail%20quality%2C%20clean%20and%20modern%20look%2C%20single%20object%20focus%2C%20the%20icon%20should%20take%20up%2070%25%20of%20the%20frame&width=100&height=100&seq=puzzle1&orientation=squarish" 
              alt="Puzzle mascot" 
              className="w-10 h-10 opacity-60"
            />
          </div>
          
          <div className="flex flex-col xl:flex-row gap-8">
            {/* Game Area */}
            <div className="xl:flex-1">
              {gameState.winner && (
                <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl p-6 mb-8 text-center">
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${gameState.winner.name === "Nobody" ? "ri-emotion-sad-line" : "ri-trophy-line"} text-purple-500 text-3xl`}></i>
                  </div>
                  <h3 className="text-white font-bold text-2xl mb-2">
                    {gameState.winner.name === "Nobody" ? "🤷‍♂️ It's a Draw! 🤷‍♂️" : `🎉 ${gameState.winner.name} Wins! 🎉`}
                  </h3>
                  <p className="text-purple-100">
                    {gameState.winner.name === "Nobody" ? "Better luck next time!" : "Word spelling champion!"}
                  </p>
                </div>
              )}
              
              {isConnected && gameState.players.length > 0 && (
                <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-purple-100 mb-6">
                  {/* Game Stats */}
                  <div className="flex justify-between items-center mb-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        Round {gameState.round}/{gameState.maxRounds}
                      </div>
                      <div className="text-sm text-gray-600">Current Round</div>
                    </div>
                    
                    <div className="text-center">
                      <div className={`text-3xl font-bold ${gameState.timeLeft <= 10 ? 'text-red-500 animate-pulse' : 'text-orange-500'}`}>
                        {gameState.timeLeft}s
                      </div>
                      <div className="text-sm text-gray-600">Time Left</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {gameState.players[gameState.currentPlayer]?.name}
                      </div>
                      <div className="text-sm text-gray-600">Current Player</div>
                    </div>
                  </div>
                  
                  {/* Word to Spell */}
                  <div className="text-center mb-8">
                    <h3 className="text-lg font-medium text-gray-700 mb-4">Spell this word:</h3>
                    <div className="text-4xl md:text-5xl font-bold text-purple-600 mb-2">
                      {gameState.currentWord}
                    </div>
                    <div className="text-sm text-gray-500">
                      Drag letters to the boxes below
                    </div>
                  </div>
                  
                  {/* Drop Zones */}
                  <div className="flex justify-center mb-8">
                    <div className="flex gap-2 flex-wrap justify-center">
                      {dropZones.map((zone, index) => (
                        <div
                          key={index}
                          className="w-16 h-16 md:w-20 md:h-20 border-4 border-dashed border-purple-300 rounded-xl flex items-center justify-center bg-purple-50 transition-colors hover:border-purple-400 hover:bg-purple-100"
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, index)}
                        >
                          {zone && (
                            <div
                              className="w-full h-full bg-gradient-to-br from-purple-400 to-pink-400 rounded-lg flex items-center justify-center text-white text-2xl font-bold cursor-pointer transition-transform hover:scale-105"
                              onClick={() => handleLetterReturn(zone)}
                            >
                              {letters.find(l => l.id === zone)?.letter}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Letter Bank */}
                  <div className="border-t border-purple-100 pt-6">
                    <h4 className="text-center text-sm font-medium text-gray-700 mb-4">Drag letters from here:</h4>
                    <div className="flex flex-wrap gap-3 justify-center">
                      {letters.filter(letter => letter.currentPosition === null).map((letter) => (
                        <div
                          key={letter.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, letter.id)}
                          onDragEnd={handleDragEnd}
                          className={`w-16 h-16 md:w-18 md:h-18 bg-gradient-to-br from-orange-400 to-red-400 rounded-xl flex items-center justify-center text-white text-2xl font-bold cursor-move transition-all duration-200 hover:scale-110 hover:shadow-lg ${
                            letter.isDragging ? 'opacity-50 scale-95' : ''
                          }`}
                        >
                          {letter.letter}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Game Controls & Players */}
            <div className="xl:w-80 space-y-6">
              {isConnected && gameState.players.length > 0 && (
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-purple-100">
                  <Button 
                    onClick={gameState.gameStarted ? () => {} : startGame}
                    disabled={gameState.gameStarted || !!gameState.winner}
                    className="w-full mb-4 py-3 text-lg"
                  >
                    {!gameState.gameStarted ? 'Start Word Puzzle' : gameState.winner ? 'Game Over' : 'Game in Progress'}
                  </Button>
                  
                  {gameState.winner && (
                    <Button 
                      onClick={resetGame}
                      variant="outline"
                      className="w-full py-3"
                    >
                      Play Again
                    </Button>
                  )}
                </div>
              )}
              
              {isConnected && gameState.players.length > 0 && (
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-purple-100">
                  <h3 className="font-bold text-gray-800 mb-4 text-lg">Players & Scores</h3>
                  
                  <div className="bg-purple-50 rounded-xl p-3 mb-4">
                    <div className="text-center">
                      <div className="font-bold text-purple-700">
                        {difficultyLevels[gameState.difficulty as keyof typeof difficultyLevels]?.name}
                      </div>
                      <div className="text-xs text-gray-600">
                        {difficultyLevels[gameState.difficulty as keyof typeof difficultyLevels]?.description}
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {gameState.players
                      .sort((a, b) => b.score - a.score)
                      .map((player, index) => (
                      <div key={player.id} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="text-lg font-bold text-gray-400">
                            #{index + 1}
                          </div>
                          <div className={`w-12 h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center border-2 border-white shadow-lg ${player.color}`}>
                            {player.isTeacher ? (
                              <i className="ri-graduation-cap-fill text-white text-lg"></i>
                            ) : (
                              <i className="ri-user-fill text-white text-lg"></i>
                            )}
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">{player.name}</span>
                            {player.isTeacher && (
                              <span className="ml-2 text-xs bg-purple-500 text-white px-2 py-1 rounded-full">Teacher</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-gray-800 text-xl">{player.score}</div>
                          <div className="text-sm text-purple-600">points</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 bg-purple-50 rounded-xl">
                    <p className="text-sm text-purple-700 font-medium">
                      🧩 Drag and drop letters to spell words correctly! Faster spelling earns bonus points!
                    </p>
                  </div>
                </div>
              )}
              
              {!isConnected && (
                <div className="bg-white rounded-2xl p-6 text-center shadow-sm border border-purple-100">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-puzzle-line text-gray-400 text-2xl"></i>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">Join Word Puzzle</h3>
                  <p className="text-gray-600 mb-4">Connect with other players to start spelling!</p>
                  <Button onClick={() => setShowJoinModal(true)} className="w-full py-3">
                    Join Game
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
